<template>
  <div>
    <v-card v-if="schedule" color="grey lighten-4" min-width="350px" flat>
      <v-toolbar :color="titleColor" dark>
        <v-toolbar-title>
          {{ title ? title : schedule.course.name }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-card class="mx-auto" max-width="400" width="250" tile>
            <v-list v-if="schedule.course" dense>
              <v-list-item-group color="primary">
                <v-list-item v-if="!canceled" @click="cancelDialog = true">
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title
                      >Cancel/Reschedule Class</v-list-item-title
                    >
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  v-if="allowDelete && schedule.rrule"
                  @click="endRecurringDialog = true"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-calendar-end</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title
                      >End Recurring Schedule
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  v-if="allowDelete"
                  @click="deleteRecurringSchedule"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-calendar-remove</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title
                      >Delete
                      {{ schedule.rrule ? "Recurring" : "" }}
                      Schedule</v-list-item-title
                    >
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-card>
        </v-menu>
      </v-toolbar>
      <v-card-text>
        <v-alert
          v-if="canceled"
          border="top"
          :elevation="2"
          colored-border
          type="warning"
        >
          This class was canceled
          <v-btn
            class="ml-5"
            :disabled="isAppLoading"
            :loading="isAppLoading"
            small
            @click="undoCancelation"
            >undo</v-btn
          >
        </v-alert>
        <v-simple-table v-if="schedule">
          <tbody>
            <tr v-if="viewCourse">
              <td>
                <v-icon class="mr-5"> mdi-shape-outline </v-icon>
                Course
              </td>
              <td>
                <router-link
                  :to="{
                    name: 'CourseDetail',
                    params: {
                      id: schedule.course ? schedule.course.id : '',
                    },
                  }"
                >
                  {{ schedule.course ? schedule.course.name : "" }}
                </router-link>
                <div>
                  {{ schedule.course ? schedule.course.description : "" }}
                </div>
              </td>
            </tr>
            <tr v-if="schedule.rrule">
              <td>
                <v-icon class="mr-5" v-if="schedule.rrule">
                  mdi-calendar-sync
                </v-icon>
                <v-icon class="mr-5" v-else> mdi-calendar-outline </v-icon>
                Schedule
              </td>
              <td>
                {{ schedule2String }}
                <router-link
                  tag="button"
                  :to="{
                    name: 'CourseSchedule',
                    params: {
                      courseId: schedule.course ? schedule.course.id : '',
                      scheduleId: schedule.id,
                    },
                  }"
                >
                  <v-btn class="ml-0 ml-2" icon>
                    <v-icon> mdi-pencil-outline </v-icon>
                  </v-btn>
                </router-link>
                <div v-if="schedule.remark">
                  {{ schedule.remark }}
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <v-icon class="mr-5"> mdi-clock-outline </v-icon>
                Time
              </td>
              <td>
                {{ scheduleDate | formatDate }}
                {{ schedule.startTime | formatTime }} -
                {{ schedule.endTime | formatTime }}

                <router-link
                  v-if="!schedule.rrule"
                  tag="button"
                  :to="{
                    name: 'CourseSchedule',
                    params: {
                      courseId: schedule.course ? schedule.course.id : '',
                      scheduleId: schedule.id,
                    },
                  }"
                >
                  <v-btn class="ml-0 ml-2" icon>
                    <v-icon> mdi-pencil-outline </v-icon>
                  </v-btn>
                </router-link>
                <div v-if="schedule.remark && !schedule.rrule">
                  {{ schedule.remark }}
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <v-icon class="mr-5"> mdi-google </v-icon>
                Calendar
              </td>
              <td>
                <div v-if="schedule.googleEventId">
                  <a @click="viewGoogleCalendar(schedule)">
                    <span v-if="schedule.googleMeetingCode"
                      >Meeting code: {{ schedule.googleMeetingCode }}</span
                    >
                    <span v-else>Event ID {{ schedule.googleEventId }}</span>
                  </a>
                </div>
                <span v-else>
                  No Google Calendar event was assigned
                  <v-btn class="ml-4" small @click="viewGoogleCalendar"
                    >Choose an Event</v-btn
                  >
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <v-icon class="mr-5"> mdi-account-outline </v-icon>
                Teachers
              </td>
              <td>
                <course-schedule-teacher
                  :courseSchedule="schedule"
                  :scheduleDate="scheduleDate"
                />
              </td>
            </tr>
            <tr v-if="meetingRecords && meetingRecords.length > 0">
              <td>
                <v-icon class="mr-5"> mdi-history </v-icon>
                Meet History
              </td>
              <td>
                <div class="my-2">
                  <div v-for="record in meetingRecords" :key="record.etag">
                    <div>
                      {{ record.displayName }}
                    </div>
                    <div class="text-caption mb-2">
                      <div v-if="record.identifier">
                        <v-icon small class="mr-2">mdi-email-outline</v-icon
                        >{{ record.identifier }}
                      </div>
                      <div>
                        <v-icon small class="mr-2">mdi-clock-outline</v-icon>
                        joined on {{ record.startTime | formatTime }} for
                        {{ timelength(record.durationSeconds) }} on
                        {{ record.deviceType }}
                      </div>
                      <div
                        v-if="
                          record.screencastSendSeconds > 0 ||
                          record.screencastRecvSeconds > 0
                        "
                      >
                        <v-icon small class="mr-2">mdi-cast-education</v-icon>
                        <span v-if="record.screencastSendSeconds > 0">
                          Screen shared for
                          {{ timelength(record.screencastSendSeconds) }}
                        </span>
                        <span v-if="record.screencastRecvSeconds > 0">
                          Screen received for
                          {{ timelength(record.screencastRecvSeconds) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>
    <div v-else>
      <v-skeleton-loader type="image" />
    </div>
    <v-dialog v-if="schedule" v-model="googleCalendarEventDialog">
      <v-card>
        <v-card-title>
          <v-spacer />
          <v-btn @click="googleCalendarEventDialog = false" icon>
            <v-icon> mdi-close </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <course-schedule-google-event
            :courseSchedule="schedule"
            @deleted="googleCalendarEventDialog = false"
            @replacedEvent="updatedEvent"
            :key="`${schedule.googleEventId}_${scheduleDate}`"
            :eventId="schedule.googleEventId"
            :calendarId="schedule.googleCalendar"
            :accountOwner="schedule.googleCalendarAccount"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog max-width="800" v-if="schedule" v-model="cancelDialog">
      <v-card>
        <v-card-title>
          <v-spacer />
          <v-btn @click="cancelDialog = false" icon>
            <v-icon> mdi-close </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <course-schedule-cancellation
            @canceledClass="updatedCancelation"
            :scheduleDate="scheduleDate"
            :courseSchedule="schedule"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog max-width="800" v-if="schedule" v-model="endRecurringDialog">
      <v-card>
        <v-card-title>
          <v-spacer />
          <v-btn @click="endRecurringDialog = false" icon>
            <v-icon> mdi-close </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="schedule && schedule.rrule != null">
            <span class="text-h6">Recurring Schedule </span>
            {{ schedule2String }}
          </div>
          <div class="my-5 text-subtitle-2">
            Choose an end date for this recurring schedule
          </div>
          <v-text-field
            prepend-icon="mdi-calendar"
            clearable
            type="date"
            :max="schedule.endDate"
            :min="schedule.startDate"
            v-model="recurringEndDate"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn @click="endRecurringSchedule" class="mb-5"> Save </v-btn>
          <v-spacer/>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import rruleUtil from '@/store/util/rruleUtil.js'

const CourseScheduleCancellation = () =>
  import("@/components/course/CourseScheduleCancellation.vue");
const CourseScheduleGoogleEvent = () =>
  import("@/components/google/CourseScheduleGoogleEvent.vue");
const CourseScheduleTeacher = () =>
  import("@/components/course/CourseScheduleTeacher.vue");

export default {
  props: {
    scheduleId: {
      type: Number,
      required: true,
    },
    scheduleDate: {
      type: [String, Date],
      required: true,
    },
    titleColor: {
      type: String,
      required: false,
      default: "primary",
    },
    title: {
      type: String,
      required: false,
    },
    allowDelete: {
      type: Boolean,
      required: false,
      default: false,
    },
    viewCourse: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      endRecurringDialog: false,
      cancelDialog: false,
      googleCalendarEventDialog: false,
      schedule: null,
      canceled: null,
      teachers: [],
      meetingRecords: [],
      recurringEndDate: null,
      timestamp: Date.now()
    };
  },
  filters: {
    formatDate(value) {
      return moment(value).format("ddd, MMM DD YYYY");
    },
    formatTime(value) {
      return moment(value, "HH:mm").format("h:mm a");
    },
  },
  components: {
    CourseScheduleGoogleEvent,
    CourseScheduleTeacher,
    CourseScheduleCancellation,
  },
  computed: {
    ...mapGetters(["navState", "isAppLoading"]),
    schedule2String(){
      return this.$options.filters.scheduleText(this.schedule);
    }
  },
  mounted() {
    console.log("mounting course schedule...");
    this.initCourseSchedule(this.scheduleId).then((schedule) => {
      this.schedule = schedule;
      const exclusions = schedule.exclusionDates;
      let excludeDates = [];
      if (exclusions && exclusions.length > 0) {
        excludeDates = exclusions.map((e) => e.date);
      }
      const eventDate = moment(this.scheduleDate);
      const date = eventDate.format("YYYY-MM-DD");
      this.canceled = excludeDates.length > 0 && excludeDates.includes(date);

      if (schedule.googleEventId || schedule.googleMeetingCode) {
        this.loadMeetingRecords();
      }
    });
  },
  methods: {
    ...mapActions(["hideDialog", "addSuccessMessage", "addErrorMessage"]),
    viewGoogleCalendar() {
      this.googleCalendarEventDialog = true;
    },
    async initCourseSchedule(courseScheduleId) {
      if (courseScheduleId) {
        return await this.$axios
          .get("/course-schedule/get-by-id", {
            params: { id: courseScheduleId },
          })
          .then((resp) => {
            return resp.data;
          });
      }
    },
    formatDate(value) {
      return moment(value).format("ddd, MMM DD YYYY");
    },
    timelength(seconds) {
      if (seconds) {
        const hrMinSec = new Date(seconds * 1000)
          .toISOString()
          .substr(11, 8)
          .split(":")
          .map((s) => parseInt(s));
        if (hrMinSec[0] < 1 && hrMinSec[1] < 1) {
          return `${hrMinSec[2] > 0 ? hrMinSec[2] + " seconds" : "N/A"}`;
        } else {
          return `${hrMinSec[0] > 0 ? hrMinSec[0] + " hr" : ""} ${
            hrMinSec[1] > 0 ? hrMinSec[1] + " mins" : ""
          }`;
        }
      } else {
        return "00:00:00";
      }
    },
    updatedEvent(newCourseSchedule) {
      this.schedule = newCourseSchedule;
      this.googleCalendarEventDialog = false;
    },
    deleteRecurringSchedule() {
      if (
        confirm(
          "Deleting this schedule will also remove teachers and students scheduled for this time.  Please confirm you want to continue deleting this schedule?"
        )
      ) {
        const data = { id: this.scheduleId };
        this.$axios.post("/course-schedule/delete", data).then(() => {
          this.$emit("deletedSchedule", this.scheduleId);
        });
      }
    },
    endRecurringSchedule() {
      const recurringWeekdays = this.schedule.daysOfWeek ? this.schedule.daysOfWeek.map(dow => parseInt(dow)):[];
      const excludeDates = this.schedule.exclusionDates ? this.schedule.exclusionDates.map(e => {return moment(e.date).format("YYYY-MM-DD")}):[];

      const rrule = rruleUtil.generateRRule(this.schedule.startDate, this.schedule.startTime, 
        this.schedule.endDate, this.schedule.endTime, recurringWeekdays);

      // rruleUtil.rruleSetWithExDates(rrule, excludeDates)

      const rruleSetWithExDates = rruleUtil.rruleSetWithExDates(rrule, excludeDates);
      const rruleSet = rruleSetWithExDates.rruleSet;
      // const exclusionObjs = rruleSetWithExDates.exclusionObjs;

      const data = {
        courseScheduleId: this.scheduleId,
        newEndDate: this.recurringEndDate,
        rrule: rrule.toString(),
        rruleWithExdate: rruleSet.toString()
      }
      this.$axios.post("/course-schedule/end-schedule", data).then((resp) => {
        this.schedule = resp.data.data;
        this.addSuccessMessage("Recurring schedule end date is updated");
        this.endRecurringDialog = false;
        this.timestamp = Date.now();
      });
    },
    undoCancelation() {
      const data = {
        courseScheduleId: this.schedule.id,
        date: moment(this.scheduleDate).format("YYYY-MM-DD"),
      };
      this.$axios
        .post("/course-schedule/undo-cancel-class", data)
        .then(() => {
          this.addSuccessMessage("Class restored");
          this.canceled = false;
          this.$emit("undoCancelation", {
            schedule: this.schedule,
            scheduleDate: this.scheduleDate,
          });
        })
        .catch(() => {
          this.addErrorMessage(
            "Oops! something went wrong, please try again later."
          );
        });
    },
    updatedCancelation(schedule, scheduleDate) {
      this.$emit("canceledClass", {
        schedule,
        scheduleDate,
      });

      this.cancelDialog = false;
    },
    loadMeetingRecords() {
      const params = {
        schedule_id: this.scheduleId,
        date: moment(this.scheduleDate).format("YYYY-MM-DD"),
      };

      this.$axios
        .get("/course-schedule/google-meeting-records", { params })
        .then((resp) => {
          console.log(resp.data);
          this.meetingRecords = resp.data.data;

          if (this.meetingRecords && this.meetingRecords.length > 0) {
            this.meetingRecords.forEach((r) => {
              r.startTime = moment(r.localTime, "HH:mm").subtract(
                r.durationSeconds,
                "seconds"
              );
            });
          }
        })
        .catch(() => {
          this.addErrorMessage("Error loading google meeting records");
        });
    },
  },
};
</script>

<style>
</style>